import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { domAnimation, LazyMotion } from 'motion/react';
import { FormEvent, lazy, Suspense, useEffect, useState } from 'react';
import { fetchWithType } from '../utils/api';
import { getCookie, removeCookie, setCookie } from '../utils/cookies';
import { getAdminUrl, locationIsKia } from '../utils/helpers';
import { ImageTypeEnum } from './components/carousel/types/CarouselTypes';
import AdaptationsExtensions from './pages/adaptationExtensions/adaptationsExtensions';
import Are from './pages/are/are';
import BehindTheSceens from './pages/behindTheSceens/behindTheSceens';
import PriceCardGrid from './pages/cardgrid/cardgrid';
import Carousel from './pages/carousel/carousel';
import ClimateBounusPVFive from './pages/climateBounusPVFive/climateBounusPVFive';
import DataCard from './pages/data-card/index';
import Disclaimers from './pages/disclaimers/disclaimers';
import FleetCalculator from './pages/fleet-calculator';
import Fleet from './pages/fleet/fleet';
import KiaEv5Campaign from './pages/kiaEv5campaign/kiaEv5campaign';
import KiaPVFiveCard from './pages/kiaPVFiveCard/kiaPVFiveCard';
import NewKiaEV6 from './pages/newKiaEv6/newKiaEv6';
import Newsletter, { ThemeEnum } from './pages/newsletter/newsletter';
import Page100 from './pages/page100/page100';
import Pdf from './pages/pdf/pdf';
import PriceCard from './pages/pricecard/pricecard';
import PrivatLeasing from './pages/privatleasing/privatleasing';
import RentAndShareApp from './pages/rentAndShareApp/rentAndShareApp';
import RentAndShareLanding from './pages/rentAndShareApp/rentAndShareLanding';
import RentAndShareSupport from './pages/rentAndShareApp/rentAndShareSupport';
import Story from './pages/story/story';
import Summer from './pages/summer/summer';
import TavlingYrkesSM from './pages/tavling/tavling';
import RenderWpPage from './wp-pages/pages';
import RenderWpModule from './wp-pages/pages/modules';

const Dashboard = lazy(() => import('./components/Dashboard'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

export const wpPages = [
  {
    slug: 'page',
    title: 'Wordpress Pages',
  },
  {
    slug: 'module',
    title: 'Wordpress Modules',
  },
];

export interface AppProps {
  kiaPage: string;
  heading?: string;
  category?: string;
  tag?: string;
  imageType?: ImageTypeEnum;
  onlyBusinessEdition?: string;
  includeBusinessEdition?: string;
  centerTitle?: boolean;
  hideTitle?: boolean;
  fields?: string;
  fuelId?: string;
  categoryIds?: string;
  pricelist?: boolean;
  theme?: ThemeEnum;
  pdfName?: string;
  description?: string;
  buttonLabel?: string;
  checkboxLabel?: string;
  emailPlaceholder?: string;
  thankYouMessage?: string;
  modelCode?: string;
  trimCodes?: string;
  modelCodes?: string;
  ids?: string;
  privateMileages?: string;
  activeTrims?: string;
  availableTrims?: string;
  uri?: string;
}

const CurrentPage = (props: Partial<AppProps>) => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const queryPage = urlParams.get('page') ?? '';

  const currentPage = props.kiaPage || queryPage;

  const filteredProps = Object.entries(props).reduce((acc, [key, value]) => {
    if (value) {
      acc[key] = value;
    }
    return acc;
  }, {});

  const LoadingFallback = () => (
    <div style={{ minHeight: '700px', width: '100%' }}></div>
  );

  switch (currentPage) {
    case 'privatleasing':
      return (
        <Suspense fallback={<LoadingFallback />}>
          <PrivatLeasing {...filteredProps} />
        </Suspense>
      );
    case 'fleet':
      return (
        <Suspense fallback={<LoadingFallback />}>
          <Fleet />
        </Suspense>
      );
    case 'story':
      return (
        <Suspense fallback={<LoadingFallback />}>
          <Story {...filteredProps} />
        </Suspense>
      );
    case 'carousel':
      return (
        <Suspense fallback={<LoadingFallback />}>
          <Carousel {...filteredProps} />
        </Suspense>
      );
    case 'are':
      return (
        <Suspense fallback={<LoadingFallback />}>
          <Are />
        </Suspense>
      );
    case 'newsletter':
      return (
        <Suspense fallback={<LoadingFallback />}>
          <Newsletter {...filteredProps} />
        </Suspense>
      );
    case 'pdf':
      return (
        <Suspense fallback={<LoadingFallback />}>
          <Pdf {...filteredProps} />
        </Suspense>
      );
    case 'rent-app':
      return (
        <Suspense fallback={<LoadingFallback />}>
          <RentAndShareApp {...filteredProps} />
        </Suspense>
      );
    case 'rent-landing':
      return (
        <Suspense fallback={<LoadingFallback />}>
          <RentAndShareLanding {...filteredProps} />
        </Suspense>
      );
    case 'rent-support':
      return (
        <Suspense fallback={<LoadingFallback />}>
          <RentAndShareSupport {...filteredProps} />
        </Suspense>
      );
    case 'pricecard':
      return (
        <Suspense>
          <PriceCard {...filteredProps} />
        </Suspense>
      );
    case 'cardgrid':
      return (
        <Suspense>
          <PriceCardGrid {...filteredProps} />
        </Suspense>
      );
    case 'disclaimers':
      return (
        <Suspense>
          <Disclaimers {...filteredProps} />
        </Suspense>
      );
    case '100':
      return (
        <Suspense>
          <Page100 {...filteredProps} />
        </Suspense>
      );
    case 'behind-the-sceens':
      return (
        <Suspense>
          <BehindTheSceens {...filteredProps} />
        </Suspense>
      );

    case 'new-kia-ev6':
      return (
        <Suspense>
          <NewKiaEV6 {...filteredProps} />
        </Suspense>
      );
    case 'kia-ev5-campaign':
      return (
        <Suspense>
          <KiaEv5Campaign {...filteredProps} />
        </Suspense>
      );
    case 'kia-pv-five-card':
      return (
        <Suspense>
          <KiaPVFiveCard {...filteredProps} />
        </Suspense>
      );
    case 'climate-bounus-pv5':
      return (
        <Suspense>
          <ClimateBounusPVFive {...filteredProps} />
        </Suspense>
      );
    case 'adaptations-extensions':
      return (
        <Suspense>
          <AdaptationsExtensions {...filteredProps} />
        </Suspense>
      );
    case 'summer':
      return (
        <Suspense>
          <Summer {...filteredProps} />
        </Suspense>
      );
    case 'fleet-calculator':
      return (
        <Suspense>
          <FleetCalculator {...filteredProps} />
        </Suspense>
      );
    case 'data-card':
      return (
        <Suspense>
          <DataCard {...filteredProps} />
        </Suspense>
      );
    case 'tavling-yrkes-sm':
      return (
        <Suspense>
          <TavlingYrkesSM />
        </Suspense>
      );
    case 'page':
      return (
        <Suspense fallback={<LoadingFallback />}>
          <RenderWpPage {...filteredProps} />
        </Suspense>
      );
    case 'module':
      return (
        <Suspense fallback={<LoadingFallback />}>
          <RenderWpModule {...filteredProps} />
        </Suspense>
      );

    default:
      if (!locationIsKia()) {
        return (
          <Suspense fallback={<LoadingFallback />}>
            <Dashboard />
          </Suspense>
        );
      } else {
        console.error('No page found');
        return null;
      }
  }
};

const KiaPage = (props: Partial<AppProps>) => {
  type AuthStatus = 'pending' | 'loggedIn' | 'loggedOut' | 'needToken';
  const [authStatus, setAuthStatus] = useState<AuthStatus>('pending');
  const [qr, setQr] = useState<string | undefined>();
  const [code, setCode] = useState('');
  const [email, setEmail] = useState<string>('');
  const deletePathParams = () => {
    window.location.href = '/';
  };

  useEffect(() => {
    const validateToken = async () => {
      const token = getCookie('x-kia-pages');
      if (token) {
        try {
          // Vefiry the token the user has stored inside thier cookies.
          const { status } = await fetchWithType(
            `${getAdminUrl()}/api/user/verify`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );
          if (status === 200) {
            setAuthStatus('loggedIn');
          } else {
            removeCookie('x-kia-pages');
            setAuthStatus('loggedOut');
          }
        } catch (error) {
          removeCookie('x-kia-pages');
          setAuthStatus('loggedOut');
        }
      } else {
        setAuthStatus('loggedOut');
      }
    };
    validateToken();
  }, []);

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = formData.get('email');
    const password = formData.get('password');
    try {
      const { data, status } = await fetchWithType<{ qr: undefined | string }>(
        `${getAdminUrl()}/api/login`,
        {
          method: 'POST',
          body: JSON.stringify({
            email,
            password,
          }),
        },
      );
      if (status === 200 && data?.qr) {
        setQr(data.qr);
        setEmail(email as string);
        setAuthStatus('needToken');
      } else if (status === 204) {
        setEmail(email as string);
        setAuthStatus('needToken');
      } else {
        alert('Fel lösenord');
      }
    } catch (error) {
      alert('Fel lösenord');
    }
  };
  const handleCode = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const codeValue = formData.get('code');
    try {
      const { data, status } = await fetchWithType<{
        token: string;
        name: string;
      }>(`${getAdminUrl()}/api/2fa`, {
        method: 'POST',
        body: JSON.stringify({
          email,
          code: codeValue,
        }),
      });
      if (status === 200 && data.token) {
        setCookie('x-kia-pages', data.token, 30);
        setAuthStatus('loggedIn');
      } else {
        alert('Fel kod');
        setCode('');
      }
    } catch (error) {
      setCode('');
      alert('Fel lösenord');
    }
  };

  if (!locationIsKia()) {
    if (authStatus === 'pending') {
      return <div style={{ minHeight: '100vh' }} />;
    }
    if (authStatus === 'needToken') {
      return (
        <div className="flex flex-col gap-4 items-center justify-center h-screen">
          <div className="bg-kia-grey max-w-sm w-full p-4 rounded-xl border border-gray-100 shadow-lg">
            <h1 className="text-4xl text-center mb-4">2FA</h1>
            <form
              key={2}
              onSubmit={(e) => handleCode(e)}
              className="grid gap-2 w-full"
            >
              <div className="grid gap-1">
                {qr && (
                  <div
                    className="mx-auto"
                    dangerouslySetInnerHTML={{ __html: qr }}
                  />
                )}
                <label htmlFor="code">Kod</label>
                <input
                  type="text"
                  id="code"
                  value={code}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCode(value);
                    if (value.length === 6) {
                      e.currentTarget.form?.requestSubmit();
                    }
                  }}
                  name="code"
                  className="border p-2"
                  placeholder="Ange 2fa kod"
                />
              </div>
            </form>
          </div>
        </div>
      );
    }
    if (authStatus === 'loggedOut') {
      return (
        <div className="flex flex-col gap-4 items-center justify-center h-screen">
          <div className="bg-kia-grey max-w-sm w-full p-4 rounded-xl border border-gray-100 shadow-lg">
            <h1 className="text-4xl text-center mb-4">Logga in</h1>
            <form
              key={1}
              onSubmit={(e) => handleLogin(e)}
              className="grid gap-2 w-full"
            >
              <div className="grid gap-1">
                <label htmlFor="email">E-mail</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="border p-2"
                  placeholder="Ange e-mail"
                />
              </div>
              <div className="grid gap-1">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  name="password"
                  className="border p-2"
                  placeholder="Ange lösenord"
                  type="password"
                />
              </div>
              <button
                type="submit"
                className="p-2 mt-4 bg-black text-white rounded-md border border-black hover:bg-white hover:text-black transition-colors"
              >
                Logga in
              </button>
            </form>
          </div>
        </div>
      );
    }
  }

  return (
    <>
      <QueryClientProvider client={queryClient}>
        {locationIsKia() ? null : (
          <div className="flex items-center px-8 justify-center bg-white border-b border-gray-100 h-[111px]">
            <button
              type="button"
              onClick={deletePathParams}
              className="text-left w-full grid place-content-center"
            >
              <p className="text-xs text-gray-400 mb-0.5">kia pages</p>
              <h1 className="text-2xl font-semibold text-gray-900 leading-tight">
                Sidor och moduler
              </h1>
            </button>
          </div>
        )}
        <LazyMotion features={domAnimation}>
          <CurrentPage {...props} />
        </LazyMotion>
      </QueryClientProvider>
    </>
  );
};

export default KiaPage;
